import { IPhoneCallCategory } from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { Pill } from '../../../../web/components-v2/Pill';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../web/components-v2/Popover';
import { SPACE, THEME } from '../../../../web/components-v2/Theme/theme';
import { hexToPillColor } from '../../../components/OutcomePill/hexToPillColor';

const PILL_GAP = SPACE[4];

export function countFittingItems(itemWidths: number[], available: number, triggerWidth: number, gap = PILL_GAP) {
	let used = 0;
	for (let index = 0; index < itemWidths.length; index++) {
		const next = used + (index ? gap : 0) + itemWidths[index];
		const isLast = index === itemWidths.length - 1;
		if (next + (isLast ? 0 : gap + triggerWidth) > available) {
			return itemWidths.length === 1 ? 1 : index;
		}
		used = next;
	}
	return itemWidths.length;
}

interface IProps {
	categories: IPhoneCallCategory[];
}

export const CategoryPills: React.FC<IProps> = ({ categories }) => {
	const containerRef = React.useRef<HTMLDivElement>(null);
	const measureRef = React.useRef<HTMLDivElement>(null);
	const [visibleCount, setVisibleCount] = React.useState(categories.length);

	React.useLayoutEffect(() => {
		const container = containerRef.current;
		const measure = measureRef.current;
		if (!container || !measure) {
			return;
		}
		const update = () => {
			const [trigger, ...pills] = Array.from(measure.children);
			setVisibleCount(
				countFittingItems(
					pills.map(pill => pill.getBoundingClientRect().width),
					container.getBoundingClientRect().width,
					trigger.getBoundingClientRect().width
				)
			);
		};
		update();
		if (typeof ResizeObserver === 'undefined') {
			return;
		}
		const observer = new ResizeObserver(update);
		observer.observe(container);
		return () => observer.disconnect();
	}, [categories]);

	if (!categories.length) {
		return null;
	}

	const renderPill = (category: IPhoneCallCategory) => (
		<Pill
			key={category.id ?? category.name}
			variant='category'
			color={hexToPillColor(category.hexColor)}
			label={category.name}
		/>
	);
	const hidden = categories.slice(visibleCount);

	return (
		<div ref={containerRef} className={css(styleSheet.container)}>
			<div ref={measureRef} className={css(styleSheet.measure)} aria-hidden='true'>
				<Button type='button' variant='link' tabIndex={-1} styles={[styleSheet.more]}>
					+{categories.length} more
				</Button>
				{categories.map(renderPill)}
			</div>
			{categories.slice(0, visibleCount).map(renderPill)}
			{hidden.length ? (
				<Popover>
					<PopoverTrigger asChild>
						<Button
							type='button'
							variant='link'
							aria-label={visibleCount ? `Show ${hidden.length} more categories` : `Show ${hidden.length} categories`}
							styles={[styleSheet.more, styleSheet.aboveRow]}
						>
							{visibleCount ? `+${hidden.length} more` : `${hidden.length} categories`}
						</Button>
					</PopoverTrigger>
					<PopoverContent align='start' styles={[styleSheet.overflow]}>
						{hidden.map(renderPill)}
					</PopoverContent>
				</Popover>
			) : null}
		</div>
	);
};

const styleSheet = StyleSheet.create({
	aboveRow: {
		position: 'relative',
		zIndex: 1,
	},
	container: {
		alignItems: 'center',
		display: 'flex',
		gap: PILL_GAP,
		minWidth: 0,
		overflow: 'hidden',
		position: 'relative',
	},
	measure: {
		display: 'flex',
		gap: PILL_GAP,
		left: 0,
		pointerEvents: 'none',
		position: 'absolute',
		top: 0,
		visibility: 'hidden',
		whiteSpace: 'nowrap',
		width: 'max-content',
	},
	more: {
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.xs,
		whiteSpace: 'nowrap',
	},
	overflow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: SPACE[8],
		maxHeight: 300,
		maxWidth: 280,
		overflowY: 'auto',
		padding: SPACE[12],
	},
});
